import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { getImageUrl } from "@/lib/images";
import { Building2, MapPin, Star, ArrowLeft, Navigation } from "lucide-react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";

const icon = L.divIcon({ className: "court-marker", html: '<div class="w-6 h-6 bg-volt rounded-full border-2 border-white/20 shadow-lg"></div>', iconSize: [24, 24], iconAnchor: [12, 12] });

function MapController({ lat, lng }) {
  const map = useMap();
  useEffect(() => { map.setView([lat, lng], 14); }, [lat, lng, map]);
  return null;
}

export default function FacilityPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/facilities/${id}`).then((r) => {
      setFacility(r.data);
      setLoading(false);
    }).catch(() => {
      setNotFound(true);
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="label-eyebrow animate-pulse">{t("common.loading")}</div>
      </div>
    );
  }

  if (notFound || !facility) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground/80 text-sm">{t("common.not_found")}</p>
          <Link to="/" className="text-volt text-xs tracking-widest uppercase mt-4 inline-block">{t("common.back_home")}</Link>
        </div>
      </div>
    );
  }

  const hasLocation = facility.lat && facility.lng;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground hover:text-volt transition-colors mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> {t("common.back")}
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-full bg-volt/10 flex items-center justify-center flex-shrink-0">
            <Building2 className="h-8 w-8 text-volt" />
          </div>
          <div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight">{facility.name}</h1>
            <div className="flex items-center gap-3 mt-1.5 text-sm text-muted-foreground">
              <span>{facility.courts?.length || 0} {t("facility_page.courts")}</span>
              {(facility.city || facility.address) && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground/80">
                  <MapPin className="h-3 w-3" />
                  {facility.city ? t(`cities.${facility.city}`, facility.city) : facility.address}
                </span>
              )}
            </div>
          </div>
        </div>

        {facility.description && (
          <p className="text-sm text-muted-foreground/80 max-w-2xl mb-10">{facility.description}</p>
        )}

        {hasLocation && (
          <div className="h-56 mb-10">
            <MapContainer center={[facility.lat, facility.lng]} zoom={14} className="h-full w-full" zoomControl={false}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[facility.lat, facility.lng]} icon={icon} />
              <MapController lat={facility.lat} lng={facility.lng} />
            </MapContainer>
          </div>
        )}

        <h2 className="label-eyebrow mb-4">{t("facility_page.all_courts")}</h2>
        {(!facility.courts || facility.courts.length === 0) ? (
          <div className="border border-dashed border-border p-16 text-center">
            <p className="text-muted-foreground/80 text-sm">{t("facility_page.no_courts")}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {facility.courts.map((court) => (
              <div
                key={court.id}
                className="border border-border bg-card overflow-hidden cursor-pointer hover:border-volt/50 transition-colors group"
                onClick={() => navigate(`/courts/${court.id}`)}
              >
                <div className="aspect-video overflow-hidden bg-background">
                  {court.images?.[0] ? (
                    <img src={getImageUrl(court.images[0], 'card')} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground/20">
                      <Building2 className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-heading text-xl tracking-tight">{court.name}</div>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground/80 tracking-wider uppercase">
                    <span>{t("home.sports." + court.sport)}</span>
                    {court.court_type && <span>· {t(`owner.court_form.court_type_${court.court_type}`, court.court_type)}</span>}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs text-muted-foreground">
                      {court.currency === "JOD" ? "JD" : court.currency}{(court.slot_pricing || {})[String(court.default_slot_duration || court.slot_duration || 60)] ?? court.price_per_slot}/{court.default_slot_duration || court.slot_duration || 60}{t("owner.court_form.min")}
                    </div>
                    {court.rating > 0 && (
                      <div className="flex items-center gap-1 text-[10px] text-yellow-400">
                        <Star className="h-3 w-3 fill-current" /> {court.rating}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {hasLocation && (
          <div className="flex justify-center mt-10">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${facility.lat},${facility.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline rounded-sm px-6 py-3 text-xs flex items-center gap-2"
            >
              <Navigation className="h-3.5 w-3.5" /> {t("court.get_directions")}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
